import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { AutoBidOrderMethod } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Input,
  PrimaryButton,
  RadioButton,
  TertiaryButton,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { Modal } from '~/components/common/Modal'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { AutoBidFormRow } from '~/pages/AdminApprovalFlow/components/AutoBidFormRow'
import { AutoBidPriceRangeHint } from '~/pages/AdminApprovalFlow/components/AutoBidPriceRangeHint'
import { AUTO_BID_VALIDATION_MESSAGE_KEY } from '~/pages/AdminApprovalFlow/constants/autoBidValidationMessageKey'
import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'
import {
  AUTO_BID_MAX_PRICE,
  AUTO_BID_MIN_PRICE,
  type AutoBidEffective,
  type AutoBidFeeAutoConsider,
  type AutoBidPriceRange,
  type AutoBidSideLocal,
  computeAutoBidEffective,
  isAutoBidFeeAutoConsiderOn,
  validateAutoBidSide,
} from '~/pages/AdminApprovalFlow/models/autoBid'

type SideKind = 'buy' | 'sell'

export type Props = {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  initialBuy: AutoBidSideLocal
  initialSell: AutoBidSideLocal
  buyMarketOrderAllowed: boolean
  sellMarketOrderAllowed: boolean
  buyPriceRange: AutoBidPriceRange
  sellPriceRange: AutoBidPriceRange
  /** 数量上限 (BidKind.QTY、円/kWh ではなく MW)。 undefined = 制限なし。 */
  buyQuantityLimit: number | undefined
  sellQuantityLimit: number | undefined
  /** 手数料自動考慮 (スポット)。 欄名と「→ 入札価格」の加減算に使う (AC-APF-041 / 043)。 */
  fee: AutoBidFeeAutoConsider
  onSave: (buy: AutoBidSideLocal, sell: AutoBidSideLocal) => void
}

/**
 * 自動入札ルール編集モーダル (AC-02/03/04/12)。
 *
 * 買 (不足時) / 売 (余剰時) の 2 列を独立に編集する。 isOpen が true になる
 * 度に initialBuy/initialSell へリセットするため、 「キャンセル」 は
 * モーダルを開く直前の状態へロールバックする (AC-12: 保存しない限り
 * ローカル state は破棄される)。
 */
export const AutoBidEditModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  initialBuy,
  initialSell,
  buyMarketOrderAllowed,
  sellMarketOrderAllowed,
  buyPriceRange,
  sellPriceRange,
  buyQuantityLimit,
  sellQuantityLimit,
  fee,
  onSave,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [buy, setBuy] = useState<AutoBidSideLocal>(initialBuy)
  const [sell, setSell] = useState<AutoBidSideLocal>(initialSell)

  // AC-12: モーダルを開く「瞬間」(閉→開の立ち上がり) のルール状態へロール
  // バックする。 initialBuy/initialSell は親レンダー毎に新しいオブジェクト参照
  // になる (autoBidSideFromRule が毎回 object literal を返す) ため、 依存配列に
  // 含めたまま「毎回 reset」すると、 モーダルを開いたまま裏で bid-limits 等の
  // 非同期データが届いて親が再レンダーした際に編集中の入力が黙って消える。
  // 立ち上がりエッジ (prevIsOpen=false → isOpen=true) でのみ reset することで、
  // 開いている間の親再レンダーでは編集内容を保持する。
  const prevIsOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setBuy(initialBuy)
      setSell(initialSell)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, initialBuy, initialSell])

  const handleSave = useCallback(() => {
    onSave(buy, sell)
  }, [onSave, buy, sell])

  // 範囲エラーは各入力欄の直下にインライン表示する (design/CLAUDE.md
  // 「入力エラーはインライン、 toast にしない」)。 保存は事前に無効化し
  // (spec AC-APF-033/034 の「保存を拒否する」)、 押せない理由は title で示す
  // (先行例: AdminBidLimits の BidLimitMatrixCard)。
  const firstError = validateAutoBidSide(buy) ?? validateAutoBidSide(sell)
  const hasError = firstError !== undefined
  const priceErrorMessage =
    firstError !== undefined
      ? t(AUTO_BID_VALIDATION_MESSAGE_KEY[firstError])
      : undefined

  return (
    <Modal
      footer={
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          {/* design の自動入札ルール modal footer は `.btn`(40px) = md。 */}
          <TertiaryButton onClick={onClose} size="md">
            {t('page:adminApprovalFlow.actions.cancel')}
          </TertiaryButton>
          <ButtonTooltip content={priceErrorMessage}>
            <PrimaryButton disabled={hasError} onClick={handleSave} size="md">
              {t('page:adminApprovalFlow.actions.save')}
            </PrimaryButton>
          </ButtonTooltip>
        </div>
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="lg"
      title={title}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {subtitle !== undefined && subtitle !== '' && (
          <div
            style={{ fontSize: fontSizes.md.value, color: eclColor.textMid }}
          >
            {subtitle}
          </div>
        )}
        <AutoBidSideForm
          effective={computeAutoBidEffective(
            buy,
            'buy',
            buyPriceRange,
            buyMarketOrderAllowed,
            fee,
          )}
          fee={fee}
          marketOrderAllowed={buyMarketOrderAllowed}
          onChange={setBuy}
          priceRange={buyPriceRange}
          quantityLimit={buyQuantityLimit}
          side="buy"
          value={buy}
        />
        <AutoBidSideForm
          effective={computeAutoBidEffective(
            sell,
            'sell',
            sellPriceRange,
            sellMarketOrderAllowed,
            fee,
          )}
          fee={fee}
          marketOrderAllowed={sellMarketOrderAllowed}
          onChange={setSell}
          priceRange={sellPriceRange}
          quantityLimit={sellQuantityLimit}
          side="sell"
          value={sell}
        />
      </div>
    </Modal>
  )
}

type AutoBidSideFormProps = {
  side: SideKind
  value: AutoBidSideLocal
  /** 単価を使えない間 (手数料自動考慮 ON) の指値は undefined で、 価格の実効値を出さない。 */
  effective: AutoBidEffective | undefined
  fee: AutoBidFeeAutoConsider
  marketOrderAllowed: boolean
  priceRange: AutoBidPriceRange
  quantityLimit: number | undefined
  onChange: (v: AutoBidSideLocal) => void
}

const AutoBidSideForm = ({
  side,
  value,
  effective,
  fee,
  marketOrderAllowed,
  priceRange,
  quantityLimit,
  onChange,
}: AutoBidSideFormProps): React.ReactElement => {
  const { t } = useTranslation()
  const sideLabel = t(`page:adminApprovalFlow.autoBid.side.${side}Label`)

  // 指値価格の範囲エラー (成行では検証しない)。 買/売の 2 組が同時に描画される
  // ため、 aria-describedby の参照先 id は useId でインスタンスごとに分ける。
  const priceError = validateAutoBidSide(value)
  const errorId = useId()

  // カバー率入力の表示用下書き。 clamp 済みの数値をそのまま controlled input に
  // 束縛すると、 既定値=上限 (100%) の時に末尾追記が即 clamp で弾かれ「100% の
  // まま動かない」状態になる。 表示は生文字列で保持し、 モデルへは clamp 済みの
  // 数値を反映する (blur で表示を clamp 済み値へ正規化)。
  const [coverageText, setCoverageText] = useState(String(value.coveragePct))
  // モデル値が外部要因 (モーダル reset / 一括編集) で変わった時だけ下書きを追従。
  // 編集中の下書きが既に同じ数値へ round-trip する間は上書きしない (入力を消さない)。
  useEffect(() => {
    setCoverageText((prev) =>
      Number(prev) === value.coveragePct ? prev : String(value.coveragePct),
    )
  }, [value.coveragePct])

  const handleCoverageChange = (raw: string): void => {
    setCoverageText(raw)
    const n = Number(raw)
    const clamped = Number.isNaN(n) ? 0 : Math.max(0, Math.min(100, n))
    onChange({ ...value, coveragePct: clamped })
  }
  const handleCoverageBlur = (): void => {
    setCoverageText(String(value.coveragePct))
  }

  const handlePriceChange = (raw: string): void => {
    const n = Number(raw)
    onChange({ ...value, limitPriceYenPerKwh: Number.isNaN(n) ? 0 : n })
  }

  return (
    <div
      style={{
        border: `1px solid ${eclColor.borderLow}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        borderRadius: '6px',
        padding: '16px',
        background: eclColor.surfaceSecondary,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* design `.apr-abform-side-title` (approval-flow.html:371) は
          font-size `--font-size-md` = 14px / weight 700 / gap `--space-2` = 8px。
          実装は 16px (lg) / 600 で 1 段大きく細かった (2026-09-04 デザイナー指摘)。 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 700,
          fontSize: fontSizes.md.value,
          color: eclColor.textHigh,
        }}
      >
        {/* 買 / 売 は DS `TradeLabel` の色付き pill (design `.ecl-trade-label--buy`
            = #007d80 / `--sell` = #b52020)。 素の span + `Surface.tertiary` の
            灰色バッジだと**買いと売りが同じ見た目**になり、 左右どちらの列かが
            色で判別できなかった (2026-09-04 デザイナー指摘)。
            共通ラッパ `TradeSideLabel` は読み上げ名と EN の 1 文字表示も持つ。 */}
        <TradeSideLabel side={side === 'buy' ? Side.BUY : Side.SELL} />
        {sideLabel}
      </div>

      {/* AC-043: 数量上限・成行可否は 設定＞入札 が優先される旨を常時表示する */}
      <div style={{ fontSize: fontSizes.sm.value, color: eclColor.textMid }}>
        <span>
          {quantityLimit !== undefined
            ? t('page:adminApprovalFlow.autoBid.modal.quantityLimitFormat', {
                value: quantityLimit.toFixed(1),
              })
            : t('page:adminApprovalFlow.autoBid.modal.quantityLimitUnlimited')}
        </span>
        ・
        <span>
          {t('page:adminApprovalFlow.autoBid.modal.marketAllowedFormat', {
            status: marketOrderAllowed
              ? t('page:adminApprovalFlow.autoBid.modal.marketAllowedYes')
              : t('page:adminApprovalFlow.autoBid.modal.marketAllowedNo'),
          })}
        </span>
      </div>

      <AutoBidFormRow
        label={t('page:adminApprovalFlow.autoBid.modal.coverage')}
      >
        {({ controlId }) => (
          <>
            {/* `aria-label` は書かない。 ラベルとの関連付けは AutoBidFormRow が配る
                id が持つので、 二重に書くと文言がずれる余地だけが残る。

                size は指定しない (= DS 既定 md = 40px)。 design
                (`admin/approval-flow.html:1543` の `ecl-input ecl-input--sm
                apr-autobid-input`) が **40px** で、 `.ecl-input--sm` は名前に反して
                `height: 40px`、 font-size と padding だけを変える modifier
                (`css/components.css:3302`)。 `.apr-autobid-input` 側にも height の
                override は無い (同 html :348 は width / text-align のみ)。
                **class 名の `sm` を DS の `size="sm"` に写すと 4px 縮む。** */}
            <Input
              id={controlId}
              max={100}
              min={0}
              onBlur={handleCoverageBlur}
              onChange={(e) => handleCoverageChange(e.target.value)}
              step={1}
              style={{ width: '80px' }}
              type="number"
              value={coverageText}
            />
            <Text color="Text.midEmphasis">
              {t('page:adminApprovalFlow.autoBid.modal.coverageUnit')}
            </Text>
          </>
        )}
      </AutoBidFormRow>

      {/* 発注方法は DS `RadioButton` を使う。 生の `<input type="radio">` は OS 既定の
          見た目になり、 design `.ecl-radio` (= DS radio) と揃わない
          (2026-09-03 デザイナー指摘)。 `radios[].label` が string で足りる 2 択なので
          slot を直接組む必要は無い (`BalanceImputationPolicy/DemandMethodRadio` は
          label に ReactNode が要るため slot 直接利用、 という別事情)。 */}
      <AutoBidFormRow
        label={t('page:adminApprovalFlow.autoBid.modal.orderMethod')}
      >
        {({ labelId }) => (
          // 単一の control ではないので `htmlFor` ではなく `aria-labelledby` で結ぶ。
          <RadioButton
            aria-labelledby={labelId}
            name={`autobid-order-method-${side}`}
            onValueChange={(details) => {
              const next = Number(details.value)
              if (!Number.isNaN(next)) {
                onChange({ ...value, orderMethod: next })
              }
            }}
            radios={[
              {
                label: t('page:adminApprovalFlow.autoBid.modal.limit'),
                value: String(AutoBidOrderMethod.LIMIT),
              },
              {
                label: t('page:adminApprovalFlow.autoBid.modal.market'),
                value: String(AutoBidOrderMethod.MARKET),
              },
            ]}
            value={String(value.orderMethod)}
          />
        )}
      </AutoBidFormRow>

      {value.orderMethod === AutoBidOrderMethod.LIMIT && (
        <>
          {/* 入力行とエラー文を縦積みにする (design/CLAUDE.md: エラー文は入力欄
              の「下」。 入力欄と同じ行に置かない)。 エラー領域は予約せず、
              エラー時だけ行が高くなる。 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <AutoBidFormRow
              label={
                isAutoBidFeeAutoConsiderOn(fee)
                  ? t('page:adminApprovalFlow.autoBid.modal.desiredPrice')
                  : t('page:adminApprovalFlow.autoBid.modal.price')
              }
            >
              {({ controlId }) => (
                <>
                  <Input
                    aria-describedby={
                      priceError !== undefined ? errorId : undefined
                    }
                    aria-invalid={priceError !== undefined}
                    id={controlId}
                    max={AUTO_BID_MAX_PRICE}
                    min={AUTO_BID_MIN_PRICE}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    step={0.01}
                    style={{ width: '96px' }}
                    type="number"
                    value={value.limitPriceYenPerKwh}
                  />
                  <Text color="Text.midEmphasis">
                    {t('page:adminApprovalFlow.autoBid.modal.priceUnit')}
                  </Text>
                </>
              )}
            </AutoBidFormRow>
            {priceError !== undefined && (
              <div
                id={errorId}
                style={{
                  fontSize: fontSizes.sm.value,
                  color: eclColor.negative,
                }}
              >
                {t(AUTO_BID_VALIDATION_MESSAGE_KEY[priceError])}
              </div>
            )}
          </div>
          {/* AC-04/AC-043: 許容範囲は範囲内でも常時表示し、 実効値は範囲外の時だけ
              警告色にする (= 成行可否に関わらず指値の clamp 判定は独立)。
              範囲エラー (保存できない理由) と実効値ヒント (bid-limits による
              clamp 後の値の説明) は役割が別なので併存させる。 */}
          <AutoBidPriceRangeHint
            effective={effective}
            priceRange={priceRange}
          />
        </>
      )}

      {!marketOrderAllowed &&
        value.orderMethod === AutoBidOrderMethod.MARKET && (
          <>
            <div
              style={{
                // 文字なので textWarning (= Text.warningHigh)。 塗り用の
                // Object.warningMid は白背景でコントラスト 1.58:1 で読めない。
                color: eclColor.textWarning,
                fontSize: fontSizes.sm.value,
                fontWeight: 600,
              }}
            >
              {t('page:adminApprovalFlow.autoBid.marketNotAllowed', {
                value: effective?.effectivePrice?.toFixed(2) ?? '',
              })}
            </div>
            <AutoBidPriceRangeHint
              effective={effective}
              priceRange={priceRange}
            />
          </>
        )}
    </div>
  )
}
